import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import type { Board } from "../api.ts";
import { C, mono } from "../theme.ts";

// Values, areas and tasks as one tree with the dependency edges drawn over it.
// Pinch and scroll zoom, pan, and labels that arrive as you get closer — the
// prototype had none of those and was unreadable past about forty nodes.

type Kind = "value" | "area" | "task";
interface N extends d3.SimulationNodeDatum { id: string; kind: Kind; label: string; short: string; colour: string; tier?: string | null; r: number }
interface L extends d3.SimulationLinkDatum<N> { kind: "holds" | "blocks" | "also" }

export function Graph({ board, onOpen }: { board: Board; onOpen: (id: string) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<string | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [hideDone, setHideDone] = useState(true);
  // Rebuilt on rotation. A graph sized once at mount is half off-screen the
  // moment the phone is turned, which is where most of this gets looked at.
  const [width, setWidth] = useState(0);
  // Height moves too: the keyboard, the collapsing browser bar and a rotation
  // all change it without changing the width.
  const [height, setHeight] = useState(0);

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { setWidth(host.current?.clientWidth ?? 0); setHeight(window.innerHeight); });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    onResize();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const W = el.clientWidth;
    // The graph fills what is left of the screen: the dynamic viewport height,
    // less where the graph starts and less the nav pill and the gesture bar.
    // Measured through a probe because --nav-clear contains env().
    const probe = document.createElement("div");
    probe.style.cssText = "position:fixed;visibility:hidden;height:var(--nav-clear)";
    document.body.appendChild(probe);
    const navClear = probe.getBoundingClientRect().height;
    probe.remove();
    const top = el.getBoundingClientRect().top + window.scrollY;
    const H = Math.max(320, Math.floor(window.innerHeight - top - navClear));
    d3.select(el).selectAll("*").remove();

    const colourOf = new Map(board.values.map((v) => [v.key, v.colour]));

    let tasks = board.tasks.filter((t) => t.state !== "parked");
    if (hideDone) tasks = tasks.filter((t) => t.state !== "done");
    if (value) tasks = tasks.filter((t) => t.value_id === value);
    if (tier) tasks = tasks.filter((t) => t.tier === tier);

    const usedAreas = new Set(tasks.map((t) => t.area_id).filter(Boolean) as string[]);
    const usedValues = new Set(tasks.map((t) => t.value_id).filter(Boolean) as string[]);
    // A secondary value is drawn even when nothing ladders to it directly —
    // otherwise the dotted link that is the whole point of it has nowhere to
    // land, and the second life an area serves silently disappears.
    for (const a of board.areas) {
      if (!usedAreas.has(a.id)) continue;
      for (const av of a.values) usedValues.add(av.value_id);
    }

    const nodes: N[] = [
      ...board.values.filter((v) => usedValues.has(v.key)).map((v): N => ({
        id: `v:${v.key}`, kind: "value", label: v.name, short: v.key, colour: v.colour, r: 24,
      })),
      ...board.areas.filter((a) => usedAreas.has(a.id)).map((a): N => {
        const primary = a.values.find((x) => x.role === "primary");
        return {
          id: `a:${a.id}`, kind: "area", label: a.name, short: a.name,
          colour: colourOf.get(primary?.value_id ?? "") ?? C.ruleStrong, r: 11,
        };
      }),
      ...tasks.map((t): N => ({
        id: t.id, kind: "task", label: t.title, short: t.id,
        colour: t.value_colour ?? C.ruleStrong, tier: t.tier,
        r: t.tier === "must" ? 7 : t.tier === "should" ? 5.5 : 4,
      })),
    ];
    const has = new Set(nodes.map((n) => n.id));

    const links: L[] = [];
    for (const a of board.areas) {
      if (!usedAreas.has(a.id)) continue;
      for (const av of a.values) {
        const target = `v:${av.value_id}`;
        if (!has.has(target)) continue;
        links.push({ source: `a:${a.id}`, target, kind: av.role === "primary" ? "holds" : "also" });
      }
    }
    for (const t of tasks) {
      const parent = t.area_id ? `a:${t.area_id}` : `v:${t.value_id}`;
      if (has.has(parent)) links.push({ source: t.id, target: parent, kind: "holds" });
      for (const b of t.blocked_by) if (has.has(b)) links.push({ source: b, target: t.id, kind: "blocks" });
    }

    const svg = d3.select(el).append("svg")
      .attr("width", W).attr("height", H).attr("viewBox", [0, 0, W, H] as any)
      .style("touch-action", "none").style("cursor", "grab");
    const root = svg.append("g");

    const sim = d3.forceSimulation<N>(nodes)
      .force("link", d3.forceLink<N, L>(links).id((d) => d.id)
        .distance((d) => (d.kind === "holds" ? 42 : d.kind === "also" ? 90 : 64))
        .strength((d) => (d.kind === "holds" ? 0.65 : d.kind === "also" ? 0.12 : 0.5)))
      .force("charge", d3.forceManyBody<N>().strength((d) => (d.kind === "value" ? -700 : d.kind === "area" ? -260 : -90)))
      .force("center", d3.forceCenter(W / 2, H / 2))
      .force("collide", d3.forceCollide<N>().radius((d) => d.r + 14));

    const link = root.append("g").selectAll("line").data(links).join("line")
      .attr("stroke", (d) => (d.kind === "blocks" ? C.alert : C.ruleStrong))
      .attr("stroke-dasharray", (d) => (d.kind === "blocks" ? "4 3" : d.kind === "also" ? "1 4" : null))
      .attr("stroke-width", (d) => (d.kind === "blocks" ? 1.3 : d.kind === "also" ? 1 : 0.9))
      .attr("stroke-opacity", (d) => (d.kind === "also" ? 0.75 : 1));

    const node = root.append("g").selectAll<SVGGElement, N>("g").data(nodes).join("g")
      .style("cursor", "pointer")
      .on("click", (_e, d) => { if (d.kind === "task") onOpen(d.id); });

    node.filter((d) => d.kind === "value").append("circle")
      .attr("r", (d) => d.r).attr("fill", (d) => d.colour).attr("fill-opacity", 0.14)
      .attr("stroke", (d) => d.colour).attr("stroke-width", 1.5);
    node.filter((d) => d.kind === "area").append("circle")
      .attr("r", (d) => d.r).attr("fill", C.ground)
      .attr("stroke", (d) => d.colour).attr("stroke-width", 1.6);
    node.filter((d) => d.kind === "task").append("circle")
      .attr("r", (d) => d.r).attr("fill", (d) => d.colour)
      .attr("fill-opacity", (d) => (d.tier === "nice" ? 0.45 : 0.9));

    const text = node.append("text")
      .attr("text-anchor", "middle")
      .attr("font-family", mono)
      .attr("fill", C.inkSoft)
      .attr("paint-order", "stroke")
      .attr("stroke", C.ground)
      .attr("stroke-width", 3)
      .attr("stroke-linejoin", "round");

    // What a label says depends on how close you are. Nothing is drawn at a
    // size it cannot be read at, and task titles only appear once there is
    // room for them.
    let scale = 1;

    function labelOf(d: N): string {
      if (d.kind === "value") return scale > 0.6 ? d.label : d.short;
      if (d.kind === "area") return scale > 1.4 ? d.label : d.short;
      if (scale > 2.1) return d.label.length > 40 ? `${d.label.slice(0, 38)}…` : d.label;
      return d.short;
    }

    // Measured, not estimated. A width guessed from the character count is
    // wrong by a tenth either way, and a tenth is the difference between two
    // labels that touch and two that do not. Remeasured only when the text or
    // the size changes, which is on relabel — not on every tick.
    const measured = new Map<string, { w: number; h: number }>();

    function relabel(k: number) {
      scale = k;
      text
        .attr("dy", (d) => (d.kind === "value" ? d.r + 15 : d.r + 11))
        .attr("font-size", (d) => (d.kind === "value" ? 11 / Math.sqrt(k) : 9.5 / Math.sqrt(k)))
        .text(labelOf);
      measured.clear();
      text.each(function (d) {
        try {
          const b = (this as SVGTextElement).getBBox();
          measured.set(d.id, { w: b.width, h: b.height });
        } catch {
          // getBBox throws on an element that has never been laid out. Fall
          // back to the estimate rather than losing the label entirely.
          const size = (d.kind === "value" ? 11 : 9.5) / Math.sqrt(k);
          measured.set(d.id, { w: labelOf(d).length * size * 0.6, h: size * 1.3 });
        }
      });
      declutter();
    }

    // Overlapping text is worse than no text: two labels on top of each other
    // read as one wrong label, and then the graph stops being trusted at all.
    //
    // Labels are laid out in priority order — values, then areas, then tasks,
    // and within tasks the heavier tier first — and any label whose box would
    // land on one already placed is dropped rather than nudged. Nudging moves
    // a label away from the node it names, which is the same problem wearing
    // a hat.
    const priority = (d: N) =>
      d.kind === "value" ? 0
        : d.kind === "area" ? 1
        : d.tier === "must" ? 2
        : d.tier === "should" ? 3
        : 4;
    const order = nodes
      .map((d, i) => ({ d, i }))
      .sort((a, b) => priority(a.d) - priority(b.d) || a.i - b.i);
    const showing = new Set<string>();

    function declutter() {
      // A label is only a candidate once its kind is close enough to be read
      // at all. Below that it is not competing for the space.
      const wants = (d: N) =>
        d.kind === "value" ? true : d.kind === "area" ? scale > 0.75 : scale > 1.15;

      const placed: { x1: number; y1: number; x2: number; y2: number }[] = [];
      showing.clear();

      for (const { d } of order) {
        if (!wants(d) || d.x == null || d.y == null) continue;
        const m = measured.get(d.id);
        if (!m) continue;
        // Screen space, not graph space: what matters is whether the reader
        // sees two strings on top of each other, and the font shrinks with
        // zoom while the layout grows. A little padding, so labels that merely
        // touch still count as colliding.
        const w = (m.w + 4) * scale;
        const h = (m.h + 2) * scale;
        const cx = d.x * scale;
        const cy = (d.y + (d.kind === "value" ? d.r + 15 : d.r + 11)) * scale;
        const box = { x1: cx - w / 2, y1: cy - h, x2: cx + w / 2, y2: cy };
        const clashes = placed.some(
          (p) => box.x1 < p.x2 && p.x1 < box.x2 && box.y1 < p.y2 && p.y1 < box.y2,
        );
        if (clashes) continue;
        placed.push(box);
        showing.add(d.id);
      }
      text.attr("opacity", (d) => (showing.has(d.id) ? 1 : 0));
    }

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.25, 6])
      .on("zoom", (e) => { root.attr("transform", e.transform); relabel(e.transform.k); });
    // Double-tap zoom is off: on a phone it fires when you meant to open a
    // task and the sheet arrives over a graph that has jumped.
    svg.call(zoom as any).on("dblclick.zoom", null);

    node.call(
      d3.drag<SVGGElement, N>()
        .on("start", (e, d) => { if (!e.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on("drag", (e, d) => { d.fx = e.x; d.fy = e.y; })
        .on("end", (e, d) => { if (!e.active) sim.alphaTarget(0); d.fx = null; d.fy = null; }) as any
    );

    function draw() {
      link
        .attr("x1", (d) => (d.source as N).x!).attr("y1", (d) => (d.source as N).y!)
        .attr("x2", (d) => (d.target as N).x!).attr("y2", (d) => (d.target as N).y!);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    }

    // The layout is settled here, synchronously, and drawn once — rather than
    // animated outwards from the centre over five seconds.
    //
    // Two reasons. A hundred nodes swirling into place on a phone is slow and
    // hard to read, and nothing is learned from watching it. And an animated
    // simulation depends on requestAnimationFrame, which the browser suspends
    // whenever the page is not compositing — a tab in the background, a
    // screen that has just locked — and a graph that is still a ball in the
    // middle when you look at it is the graph "not working".
    sim.stop();
    for (let i = 0; i < 260; i++) sim.tick();
    draw();
    relabel(1);

    // Collision is recomputed as the layout moves rather than on every tick.
    // A hundred boxes intersected sixty times a second is the one thing here
    // that would make an older phone stutter. This only runs while a node is
    // being dragged, which is the only thing that restarts the simulation.
    let ticks = 0;
    sim.on("tick", () => {
      draw();
      if (++ticks % 6 === 0) declutter();
    });
    sim.on("end", declutter);

    return () => { sim.stop(); };
  }, [board, value, tier, hideDone, width, height, onOpen]);

  const chip = (on: boolean, colour?: string): React.CSSProperties => ({
    fontFamily: mono, fontSize: 11, padding: "4px 10px", borderRadius: 99, cursor: "pointer",
    border: `1px solid ${on ? (colour ?? C.ink) : C.rule}`,
    background: on ? (colour ? `${colour}22` : C.card) : "transparent",
    color: on ? C.ink : C.inkFaint, whiteSpace: "nowrap",
  });

  return (
    <div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", padding: "12px 18px 12px", margin: "-12px 0 -2px", scrollbarWidth: "none" }}>
        <span className="hit" onClick={() => setValue(null)} style={chip(value === null)}>all</span>
        {board.values.map((v) => (
          <span className="hit" key={v.key} onClick={() => setValue(value === v.key ? null : v.key)} style={chip(value === v.key, v.colour)}>
            {v.key}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, padding: "0 18px 8px" }}>
        {["must", "should", "nice"].map((t) => (
          <span className="hit" key={t} onClick={() => setTier(tier === t ? null : t)} style={chip(tier === t)}>{t}</span>
        ))}
        <span className="hit" onClick={() => setHideDone(!hideDone)} style={chip(!hideDone)}>show done</span>
      </div>
      <div ref={host} style={{ width: "100%" }} />
      <p style={{ fontFamily: mono, fontSize: 10, color: C.inkGhost, padding: "0 18px", lineHeight: 1.7 }}>
        rings = values · hollow = areas · dots = tasks · <span style={{ color: C.alert }}>dashed</span> = waiting on ·
        dotted = a second value · pinch or scroll to zoom, drag to move ·
        labels arrive as you get closer
      </p>
    </div>
  );
}
